/**
 * Warteschlange für Änderungen ohne Netz.
 *
 * Kann eine Änderung (Rezept speichern, bewerten, Merkliste, Kochprotokoll)
 * nicht gesendet werden, landet sie hier und wird gespeichert. Sobald wieder
 * Verbindung besteht, gehen die Einträge der Reihe nach an den Server.
 *
 * - Mehrere Änderungen am selben Gegenstand werden zusammengefasst (gleicher
 *   Schlüssel); bei Rezepten bleibt dabei der ursprüngliche Bearbeitungsstand
 *   erhalten, damit Konflikte erkannt werden.
 * - Meldet der Server einen Konflikt (409), bleibt der Eintrag stehen, bis
 *   die Person entscheidet: eigene Fassung speichern oder verwerfen.
 */
import { useSyncExternalStore } from 'react';
import { ApiError, api, isOffline } from '../api/client.ts';

/** Ein wartender Eintrag. */
export interface OutboxEntry {
  readonly id: string;
  /** Zusammenfassen gleicher Änderungen, zum Beispiel „favorite:<Rezept>“. */
  readonly key: string;
  readonly method: 'POST' | 'PUT' | 'DELETE';
  readonly path: string;
  readonly body: Record<string, unknown> | null;
  /** Beschreibung für die Anzeige, zum Beispiel „Rezept „Brot“ speichern“. */
  readonly label: string;
  readonly createdAt: string;
  readonly status: 'waiting' | 'conflict' | 'failed';
  readonly message: string | null;
}

/** Name im Gerätespeicher. */
const STORAGE_KEY = 'kochbuch.warteschlange';

/** Abstand für erneute Versuche. */
const RETRY_MS = 30_000;

let entries: readonly OutboxEntry[] = load();
const listeners = new Set<() => void>();
let running = false;
let retry: number | null = null;

/** Liest die Warteschlange aus dem Gerät. */
function load(): OutboxEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? [] : (JSON.parse(raw) as OutboxEntry[]);
  } catch {
    return [];
  }
}

/** Speichert die Warteschlange und benachrichtigt die Anzeige. */
function commit(next: readonly OutboxEntry[]): void {
  entries = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Speicher voll: Die Änderungen bleiben bis zum Neuladen im Speicher.
  }
  for (const listener of listeners) {
    listener();
  }
}

/** Plant einen erneuten Versuch. */
function scheduleRetry(): void {
  if (retry !== null || typeof window === 'undefined') {
    return;
  }
  retry = window.setTimeout(() => {
    retry = null;
    void processOutbox();
  }, RETRY_MS);
}

/** Sendet einen Eintrag. */
async function send(entry: OutboxEntry): Promise<unknown> {
  switch (entry.method) {
    case 'POST':
      return api.post(entry.path, entry.body ?? undefined);
    case 'PUT':
      return api.put(entry.path, entry.body ?? undefined);
    default:
      return api.delete(entry.path);
  }
}

/** Arbeitet die wartenden Einträge der Reihe nach ab. */
export async function processOutbox(): Promise<void> {
  if (running) {
    return;
  }
  running = true;
  try {
    for (const entry of entries) {
      if (entry.status !== 'waiting') {
        continue;
      }
      try {
        await send(entry);
        commit(entries.filter((candidate) => candidate.id !== entry.id));
      } catch (caught) {
        if (isOffline(caught)) {
          scheduleRetry();
          return;
        }
        const status = caught instanceof ApiError && caught.status === 409 ? 'conflict' : 'failed';
        const message = caught instanceof Error ? caught.message : 'Unbekannter Fehler.';
        commit(
          entries.map((candidate) =>
            candidate.id === entry.id ? { ...candidate, status, message } : candidate,
          ),
        );
      }
    }
  } finally {
    running = false;
  }
}

/** Nimmt einen Eintrag auf; ein gleicher Schlüssel ersetzt den älteren. */
export function enqueue(entry: Omit<OutboxEntry, 'id' | 'createdAt' | 'status' | 'message'>): void {
  const existing = entries.find((candidate) => candidate.key === entry.key && candidate.status === 'waiting');
  // Bei Rezepten zählt der Stand, auf dem die erste Bearbeitung begann.
  const base = existing?.body?.baseUpdatedAt;
  const body =
    base !== undefined && entry.body !== null ? { ...entry.body, baseUpdatedAt: base } : entry.body;
  const next: OutboxEntry = {
    ...entry,
    body,
    id: existing?.id ?? crypto.randomUUID(),
    createdAt: existing?.createdAt ?? new Date().toISOString(),
    status: 'waiting',
    message: null,
  };
  commit(
    existing === undefined
      ? [...entries, next]
      : entries.map((candidate) => (candidate.id === existing.id ? next : candidate)),
  );
  scheduleRetry();
}

/**
 * Sendet sofort; ohne Netz kommt die Änderung in die Warteschlange.
 * @returns Die Antwort des Servers oder null, wenn die Änderung wartet.
 */
export async function sendOrQueue<T>(
  entry: Omit<OutboxEntry, 'id' | 'createdAt' | 'status' | 'message'>,
): Promise<{ queued: false; data: T } | { queued: true }> {
  try {
    const data = (await send({ ...entry, id: '', createdAt: '', status: 'waiting', message: null })) as T;
    return { queued: false, data };
  } catch (caught) {
    if (isOffline(caught)) {
      enqueue(entry);
      return { queued: true };
    }
    throw caught;
  }
}

/** Löst einen Konflikt oder Fehler: eigene Fassung erneut senden oder verwerfen. */
export function resolveEntry(id: string, choice: 'mine' | 'discard'): void {
  if (choice === 'discard') {
    commit(entries.filter((entry) => entry.id !== id));
    return;
  }
  commit(
    entries.map((entry) => {
      if (entry.id !== id) {
        return entry;
      }
      // Ohne Bearbeitungsstand überschreibt der Server die neuere Fassung.
      const { baseUpdatedAt: _ignored, ...body } = entry.body ?? {};
      return { ...entry, body: entry.body === null ? null : body, status: 'waiting', message: null };
    }),
  );
  void processOutbox();
}

/** Wartende Änderungen für einen Pfad, zum Beispiel um einen Hinweis zu zeigen. */
export function pendingFor(path: string): OutboxEntry[] {
  return entries.filter((entry) => entry.path === path);
}

/** Abo für useSyncExternalStore. */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Alle Einträge für die Anzeige. */
export function useOutbox(): readonly OutboxEntry[] {
  return useSyncExternalStore(subscribe, () => entries);
}

/** Leert die Warteschlange, zum Beispiel beim Abmelden. */
export function clearOutbox(): void {
  commit([]);
}

/** Startet die Abarbeitung beim Laden der App und bei jeder neuen Verbindung. */
export function startOutbox(): void {
  window.addEventListener('online', () => void processOutbox());
  void processOutbox();
}
